import { useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import AssessmentIcon from "@material-ui/icons/Assessment";
import DescriptionIcon from "@material-ui/icons/Description";
import ReceiptIcon from "@material-ui/icons/Receipt";
import LayersIcon from "@material-ui/icons/Layers";
import OpacityIcon from "@material-ui/icons/Opacity";

import FormDatePicker from "../../shared/form-date-picker";
import {
  generateDailySummaryReport,
  generateDigitalTicketReport,
  generateProjectsReport,
} from "../../../services/ticketService";
import StorageAreaSummaryReportPanel from "../storage-area/storage-area-summary-report-panel";
import EmailedReportPanel from "../emailed-report-panel";
import ReportCard from "../report-card";
import {
  sendContainmentPadSummaryReport,
  sendStockpileSummaryReport,
} from "../../../services/storageAreaService";
import { DigitalTicketStream } from "../../../interface/forms/DigitalTicketReportProps";
import { StreamOptions } from "../../../utils/constants";
import { ReportRunResult } from "../../../interface/ReportRunResult";
import { ReportRunStatus } from "../../../interface/ReportRunStatus";
import { ReportCardStatus } from "../../../interface/ReportCardStatus";
import { formatDate } from "../../../utils/date-helper";
import { useReportGridStyles } from "../../../utils/report-layout";

// Every stream in one PDF, ahead of the shared per-stream choices.
const digitalTicketStreams: { value: DigitalTicketStream; label: string }[] = [
  { value: "all", label: "All Streams" },
  ...StreamOptions,
];

const chipLabels: Record<ReportRunStatus, string> = {
  running: "Running",
  success: "Last run: sent",
  empty: "Last run: nothing found",
  error: "Last run: failed",
};

const chipFor = (result?: ReportRunResult): ReportCardStatus | undefined =>
  result ? { status: result.status, label: chipLabels[result.status] } : undefined;

// "If left empty, today is used" — said once rather than under each date picker.
const DefaultsToToday = () => (
  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
    If left empty, today&apos;s date will be used automatically.
  </Typography>
);

const TemplateReportForm = () => {
  const classes = useReportGridStyles();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [digitalTicketDate, setDigitalTicketDate] = useState<Date | null>(null);
  const [digitalTicketStream, setDigitalTicketStream] =
    useState<DigitalTicketStream>("all");

  // One card open at a time: an expanded report is tall enough that a second one pushes
  // it off screen, and the grid reflows around whichever is open.
  const [expanded, setExpanded] = useState<string | null>(null);
  const [runs, setRuns] = useState<Record<string, ReportRunResult>>({});

  const toggle = (id: string) => () =>
    setExpanded((open) => (open === id ? null : id));

  const recordRun = (id: string) => (result: ReportRunResult) =>
    setRuns((previous) => ({ ...previous, [id]: result }));

  const cardProps = (id: string) => ({
    expanded: expanded === id,
    onToggle: toggle(id),
    lastRun: chipFor(runs[id]),
  });

  const dateFact = (label: string, date: Date | null) => [
    { label, value: date ? formatDate(date, false) : "Today" },
  ];

  return (
    <Box className={classes.root}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <ReportCard
            title="Daily Summary Report"
            description="An Excel log of every ticket received on the selected date across all streams, alongside any tickets rejected that day."
            icon={<AssessmentIcon />}
            {...cardProps("daily")}
          >
            <EmailedReportPanel
              title="Daily Summary Report"
              emptyMessage="No tickets were found for the selected date."
              facts={dateFact("Date received", selectedDate)}
              send={(emails) =>
                generateDailySummaryReport(selectedDate || undefined, emails)
              }
              onRunStateChange={recordRun("daily")}
            >
              <Grid item xs={12}>
                <FormDatePicker
                  label="Select Date (optional)"
                  value={selectedDate}
                  onChange={(date: any) => setSelectedDate(date)}
                  clearable
                />
                <DefaultsToToday />
              </Grid>
            </EmailedReportPanel>
          </ReportCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ReportCard
            title="Digital Tickets"
            description="A PDF of the digital tickets received on the selected date, each rendered as it appears in the portal."
            icon={<ReceiptIcon />}
            {...cardProps("digital")}
          >
            <EmailedReportPanel
              title="Digital Tickets"
              emptyMessage={
                digitalTicketStream === "all"
                  ? "No tickets were received on the selected date."
                  : "No tickets were received on the selected date for that stream."
              }
              sendLabel="Send Digital Tickets"
              sendingLabel="Sending Digital Tickets..."
              facts={[
                ...dateFact("Date received", digitalTicketDate),
                {
                  label: "Workstream",
                  value:
                    digitalTicketStreams.find(
                      (s) => s.value === digitalTicketStream,
                    )?.label ?? digitalTicketStream,
                },
              ]}
              send={(emails) =>
                generateDigitalTicketReport(
                  digitalTicketDate || undefined,
                  digitalTicketStream,
                  emails,
                )
              }
              onRunStateChange={recordRun("digital")}
            >
              <Grid item xs={12}>
                <FormControl variant="outlined" fullWidth>
                  <InputLabel id="digital-ticket-stream-label">
                    Workstream
                  </InputLabel>
                  <Select
                    labelId="digital-ticket-stream-label"
                    name="digitalTicketStream"
                    label="Workstream"
                    value={digitalTicketStream}
                    onChange={(e) =>
                      setDigitalTicketStream(
                        e.target.value as DigitalTicketStream,
                      )
                    }
                  >
                    {digitalTicketStreams.map((s) => (
                      <MenuItem key={s.value} value={s.value}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormDatePicker
                  label="Date Received (optional)"
                  value={digitalTicketDate}
                  onChange={(date: any) => setDigitalTicketDate(date)}
                  clearable
                />
                <DefaultsToToday />
              </Grid>
            </EmailedReportPanel>
          </ReportCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ReportCard
            title="Projects Report"
            description="The projects export workbook, emailed to you — or to the configured fallback recipient when no email is available."
            icon={<DescriptionIcon />}
            {...cardProps("projects")}
          >
            <EmailedReportPanel
              title="Projects Report"
              emptyMessage="There are no projects to report on."
              sendLabel="Send Projects Report"
              sendingLabel="Sending Projects Report..."
              send={(emails) => generateProjectsReport(emails)}
              onRunStateChange={recordRun("projects")}
            />
          </ReportCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ReportCard
            title="Small Quantities Summary Report"
            description="An Excel log of every stockpile closed with a lab report between the selected dates, listing all of its loads with the sampled loads flagged and a permanent link to the lab report."
            icon={<LayersIcon />}
            {...cardProps("stockpiles")}
          >
            <StorageAreaSummaryReportPanel
              title="Small Quantities Summary Report"
              emptyMessage="No stockpiles were closed with a lab report in the selected dates."
              send={sendStockpileSummaryReport}
              onRunStateChange={recordRun("stockpiles")}
            />
          </ReportCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ReportCard
            title="Hydrovac Summary Report"
            description="An Excel log of every containment pad closed with a lab report between the selected dates, listing all of its loads with a permanent link to the lab report."
            icon={<OpacityIcon />}
            {...cardProps("pads")}
          >
            <StorageAreaSummaryReportPanel
              title="Hydrovac Summary Report"
              emptyMessage="No containment pads were closed with a lab report in the selected dates."
              send={sendContainmentPadSummaryReport}
              onRunStateChange={recordRun("pads")}
            />
          </ReportCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TemplateReportForm;
